import 'react-dates/initialize';
import { DateRangePicker } from 'react-dates';
import './camp.css';

import { useEffect, useRef, useState } from 'react';
import moment from 'moment';

import Navigation from '../../../assets/icons/trvto_navigation.png';

import TypingAnimation from '../../../components/typingAnimation/TypingAni';
import CampGuestSelector from '../../../components/campGuestSelector/CampGuestSelector';

import { useNavigate, useSearchParams } from 'react-router-dom';
import { debouncedHandleAutoComplete } from '../handleAutcomplete';
import { readCampStay, saveCampStay } from '../../../utils/campStay';

const hasValidGuests = (guestCounts) =>
    Boolean(guestCounts) && Number(guestCounts.adults || 0) + Number(guestCounts.children || 0) > 0;

// where the results page lives (v2 passes '/v2/search-result')
const DEFAULT_RESULTS_BASE = '/search-result';

// mobileCalendar: 'fullscreen' (old site) or 'popup' (small calendar under the dates, like desktop)
const Camp = ({ className, isScrolled, resultsBase = DEFAULT_RESULTS_BASE, mobileCalendar = 'fullscreen' }) => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    /* =========================================================
       STATE
    ========================================================= */

    const [focusedInput, setFocusedInput] = useState(null);

    const [openGuests, setOpenGuests] = useState(false);

    const [guestsConfirmed, setGuestsConfirmed] = useState(false);

    const [searchAttempted, setSearchAttempted] = useState(false);

    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    const [searchField, setSearchField] = useState('');

    const [autoCompleteData, setAutoCompleteData] = useState({
        locations: [],
        stays: []
    });

    const [showAutocomplete, setShowAutocomplete] = useState(false);

    const [limit, setLimit] = useState(9);

    const [startDate, setStartDate] = useState(null);

    const [endDate, setEndDate] = useState(null);

    const [guests, setGuests] = useState({
        adults: 1,
        children: 0,
        pets: 0
    });

    /* =========================================================
       REFS
    ========================================================= */

    const inputFo = useRef(null);

    const autoCompleteRef = useRef(null);

    /*
     * Used when a place is selected.
     * Prevents the calendar from immediately losing focus
     * during the autocomplete -> calendar transition.
     */
    const openCalendarAfterSelection = useRef(false);

    /* =========================================================
       TYPING ANIMATION
    ========================================================= */

    const tripText = ['Where do you want to stay', 'Search', 'Explore'];

    const headerTypingAniStyle = {
        searchContainer: {
            top: 0,
            left: 0
        }
    };

    /* =========================================================
       WIDTH
    ========================================================= */

    const homePaths = ['/', '/camp', '/trip'];

    const isWidthRequired = homePaths.includes(window.location.pathname);

    /* =========================================================
       RESIZE
    ========================================================= */

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    /* =========================================================
       LOAD SEARCH PARAMS
    ========================================================= */

    useEffect(() => {
        /*
         * Dates + guests from the URL (checkin=2026-10-07&adults=2 ...),
         * else this session's last camp search (utils/campStay.js).
         */
        // place: URL, else the last camp search (details page: the booking tab
        // puts the searched place / the camp's city in the URL)
        const stay = readCampStay(searchParams);
        const place = stay.place;

        if (place) {
            setSearchField(place);
        }

        if (stay.checkin && stay.checkout) {
            setStartDate(moment(stay.checkin, 'YYYY-MM-DD'));
            setEndDate(moment(stay.checkout, 'YYYY-MM-DD'));
        }

        if (stay.found) {
            setGuests({ adults: stay.adults, children: stay.children, pets: stay.pets });
            setGuestsConfirmed(true);
        }
    }, [searchParams]);

    /* =========================================================
       CLOSE AUTOCOMPLETE OUTSIDE
    ========================================================= */

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (autoCompleteRef.current && !autoCompleteRef.current.contains(event.target)) {
                setShowAutocomplete(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    /* =========================================================
       HIDE AUTOCOMPLETE ON SCROLL
    ========================================================= */

    useEffect(() => {
        const handleScroll = () => {
            setShowAutocomplete(false);

            setAutoCompleteData({
                locations: [],
                stays: []
            });
        };

        window.addEventListener('scroll', handleScroll, {
            passive: true
        });

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    /* =========================================================
       AUTOCOMPLETE
    ========================================================= */

    const runAutocomplete = (value = '') => {
        const mobile = window.innerWidth < 768;

        const autocompleteLimit = mobile ? 5 : 10;

        const catlim = mobile ? 2 : 4;

        debouncedHandleAutoComplete({
            type: 'camp',
            value,
            setAutoCompleteData,
            limit: autocompleteLimit,
            catlim
        });
    };

    /* =========================================================
       LOCATION FOCUS
    ========================================================= */

    const handleCampFocus = () => {
        setShowAutocomplete(true);

        runAutocomplete(searchField || '');
    };

    /* =========================================================
       LOCATION INPUT
    ========================================================= */

    const handleSearchType = (value) => {
        setSearchField(value);

        setShowAutocomplete(true);

        runAutocomplete(value);
    };

    /* =========================================================
       LOCATION CLICK
    ========================================================= */

    const handleSpan = () => {
        setShowAutocomplete(true);

        inputFo.current?.focus();

        runAutocomplete(searchField || '');
    };

    /* =========================================================
       SELECT LOCATION
       
       PLACE -> CHECK-IN CALENDAR
    ========================================================= */

    const handleAutocompleteClick = (item) => {
        if (!item) {
            return;
        }

        setSearchField(item);

        setShowAutocomplete(false);

        setAutoCompleteData({
            locations: [],
            stays: []
        });

        setOpenGuests(false);

        setSearchAttempted(false);

        openCalendarAfterSelection.current = true;

        /*
         * Give the autocomplete click event time to finish.
         */
        window.setTimeout(() => {
            setFocusedInput('startDate');
        }, 150);
    };

    /* =========================================================
       RECENT SEARCH CLICK

       Dates still in the future -> search again straight away.
       Old dates -> fill the place and open the calendar.
    ========================================================= */

    const handleRecentCampClick = (recent) => {
        const recentPlace = recent?.place;

        if (!recentPlace) {
            return;
        }

        const data = recent?.searchData || {};
        const checkIn = moment(data.checkIn, 'YYYY-MM-DD');
        const checkOut = moment(data.checkOut, 'YYYY-MM-DD');
        const savedGuests = data.guests || {};

        const datesStillValid =
            checkIn.isValid() && checkOut.isValid() && checkIn.isSameOrAfter(moment(), 'day') && checkOut.isAfter(checkIn);

        if (!datesStillValid) {
            handleAutocompleteClick(recentPlace);
            return;
        }

        const nextGuests = {
            ...guests,
            adults: Number(savedGuests.adults) || 1,
            children: Number(savedGuests.children) || 0,
            pets: Number(savedGuests.pets) || 0
        };

        setSearchField(recentPlace);
        setStartDate(checkIn);
        setEndDate(checkOut);
        setGuests(nextGuests);
        setGuestsConfirmed(true);
        setShowAutocomplete(false);
        setAutoCompleteData({ locations: [], stays: [] });

        const params = new URLSearchParams();
        params.append('place', recentPlace);
        params.append('checkin', checkIn.format('YYYY-MM-DD'));
        params.append('checkout', checkOut.format('YYYY-MM-DD'));
        params.append('adults', nextGuests.adults);
        params.append('children', nextGuests.children);
        params.append('pets', nextGuests.pets);

        saveCampStay({ place: recentPlace, checkin: checkIn, checkout: checkOut, ...nextGuests });

        navigate(`${resultsBase}/camp?${params.toString()}`, {
            state: { limit: 9 }
        });
    };

    /* =========================================================
       DATE CHANGE
    ========================================================= */

    const handleDatesChange = ({ startDate, endDate }) => {
        setStartDate(startDate);

        setEndDate(endDate);

        /*
         * CHECK-IN SELECTED
         *
         * Automatically move to CHECK-OUT.
         */
        if (startDate && !endDate) {
            setFocusedInput('endDate');

            return;
        }

        /*
         * BOTH DATES SELECTED
         */
        if (startDate && endDate) {
            setFocusedInput(null);

            window.setTimeout(() => {
                setOpenGuests(true);
            }, 150);
        }
    };

    /* =========================================================
       DATE FOCUS CHANGE
    ========================================================= */

    const handleDateFocusChange = (nextFocusedInput) => {
        /*
         * react-dates can briefly return null while
         * switching focus after autocomplete selection.
         */
        if (nextFocusedInput === null && openCalendarAfterSelection.current) {
            openCalendarAfterSelection.current = false;

            return;
        }

        setFocusedInput(nextFocusedInput);
    };

    /* =========================================================
       SEARCH
    ========================================================= */

    const handleSearch = (query) => {
        const valueToSearch = (query || searchField).trim();

        const hasGuests = guestsConfirmed && hasValidGuests(guests);

        setSearchAttempted(true);

        /*
         * LOCATION
         */
        if (!valueToSearch) {
            inputFo.current?.focus();

            return;
        }

        /*
         * DATE
         */
        if (!startDate || !endDate) {
            setFocusedInput(!startDate ? 'startDate' : 'endDate');

            return;
        }

        /*
         * GUESTS
         */
        if (!hasGuests) {
            setOpenGuests(true);

            return;
        }

        const searchLimit = 9;

        setLimit(searchLimit);

        setShowAutocomplete(false);

        setAutoCompleteData({
            locations: [],
            stays: []
        });

        setSearchField(valueToSearch);

        setFocusedInput(null);

        /* =====================================================
           QUERY
        ===================================================== */

        const params = new URLSearchParams();

        params.append('place', valueToSearch);

        params.append('checkin', startDate.format('YYYY-MM-DD'));

        params.append('checkout', endDate.format('YYYY-MM-DD'));

        params.append('adults', guests.adults);
        params.append('children', guests.children || 0);
        params.append('pets', guests.pets || 0);

        // remembered for the details + booking pages (utils/campStay.js)
        saveCampStay({ place: valueToSearch, checkin: startDate, checkout: endDate, ...guests });

        navigate(`${resultsBase}/camp?${params.toString()}`, {
            state: {
                limit: searchLimit
            }
        });
    };

    /* =========================================================
       ENTER
    ========================================================= */

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleSearch(searchField);
        }
    };

    /* =========================================================
       RENDER
    ========================================================= */

    return (
        <div
            className={`
                campSearchWrapper
                ${!isWidthRequired || isScrolled ? 'campSearchWidth' : ''}
                ${className || ''}
            `}
        >
            {/* =================================================
                LOCATION
            ================================================= */}

            <div className='campCol campCol-location' ref={autoCompleteRef}>
                <span className='campColLabel'>Where</span>

                <div className='campColValueRow'>
                    <img src={Navigation} alt='location' className='campColIcon' />

                    <div className='campLocationInputWrapper'>
                        <input
                            ref={inputFo}
                            value={searchField}
                            onFocus={handleCampFocus}
                            onClick={handleSpan}
                            onChange={(e) => handleSearchType(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder=''
                            className={`
                                campSearch
                                ${searchAttempted && !searchField.trim() ? 'campSearchInvalid' : ''}
                            `}
                        />

                        {/* TYPING ANIMATION */}

                        {!searchField && (
                            <div className='campTypingWrapper' onClick={handleSpan}>
                                <TypingAnimation
                                    texts={tripText}
                                    handleSpan={handleSpan}
                                    seConStyle={headerTypingAniStyle.searchContainer}
                                />
                            </div>
                        )}

                        {/* AUTOCOMPLETE */}

                        {showAutocomplete && (
                            <div className='campAutocompleteDropdown'>
                                {/* RECENT SEARCHES (empty box only) */}

                                {!searchField.trim() && autoCompleteData.recentSearches?.length > 0 && (
                                    <div className='campAutocompleteSection'>
                                        <div className='campAutocompleteHeading'>Recent searches</div>

                                        {autoCompleteData.recentSearches.map((recent, index) => {
                                            const data = recent?.searchData || {};
                                            const dates =
                                                data.checkIn && data.checkOut
                                                    ? `${moment(data.checkIn).format('D MMM')} – ${moment(data.checkOut).format('D MMM')}`
                                                    : '';
                                            const people = Number(data?.guests?.adults || 0) + Number(data?.guests?.children || 0);

                                            return (
                                                <button
                                                    type='button'
                                                    key={`recent-${recent?.id || index}`}
                                                    className='campAutocompleteItem'
                                                    onMouseDown={(e) => {
                                                        e.preventDefault();
                                                        handleRecentCampClick(recent);
                                                    }}
                                                >
                                                    <div className='campAutocompleteIcon'>🕘</div>

                                                    <div className='campAutocompleteContent'>
                                                        <strong>{recent?.place}</strong>

                                                        <span>
                                                            {[dates, people ? `${people} guest${people > 1 ? 's' : ''}` : '']
                                                                .filter(Boolean)
                                                                .join(' · ')}
                                                        </span>
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}

                                {/* LOCATIONS */}

                                {autoCompleteData.locations?.length > 0 && (
                                    <div className='campAutocompleteSection'>
                                        <div className='campAutocompleteHeading'>Locations</div>

                                        {autoCompleteData.locations.map((location, index) => (
                                            <button
                                                type='button'
                                                key={`
                                                        location-
                                                        ${location.name}-
                                                        ${index}
                                                    `}
                                                className='campAutocompleteItem'
                                                onMouseDown={(e) => {
                                                    e.preventDefault();

                                                    handleAutocompleteClick(location.name);
                                                }}
                                            >
                                                <div className='campAutocompleteIcon'>
                                                    <img src={Navigation} alt='' />
                                                </div>

                                                <div className='campAutocompleteContent'>
                                                    <strong>{location.name}</strong>

                                                    <span>
                                                        {location.state}
                                                        {' · '}
                                                        {location.stayCount}{' '}
                                                        {location.stayCount === 1 ? 'stay' : 'stays'}
                                                    </span>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                )}

                                {/* STAYS */}

                                {autoCompleteData.stays?.length > 0 && (
                                    <div className='campAutocompleteSection'>
                                        <div className='campAutocompleteHeading'>Stays</div>

                                        {autoCompleteData.stays.map((stay) => (
                                            <button
                                                type='button'
                                                key={stay.id}
                                                className='
                                                        campAutocompleteItem
                                                        campStayItem
                                                    '
                                                onMouseDown={(e) => {
                                                    e.preventDefault();

                                                    handleAutocompleteClick(stay.name);
                                                }}
                                            >
                                                <div className='campStayImage'>
                                                    {stay.image ? (
                                                        <img src={stay.image} alt={stay.name} />
                                                    ) : (
                                                        <div className='campStayNoImage'>🏕️</div>
                                                    )}
                                                </div>

                                                <div className='campAutocompleteContent'>
                                                    <strong>{stay.name}</strong>

                                                    <span>{stay.location}</span>

                                                    {stay.price !== undefined && stay.price !== null && (
                                                        <small>
                                                            From {stay.currency === 'INR' ? '₹' : stay.currency}{' '}
                                                            {Number(stay.price).toLocaleString('en-IN')}
                                                            {' / night'}
                                                        </small>
                                                    )}
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                )}

                                {!autoCompleteData.locations?.length &&
                                    !autoCompleteData.stays?.length &&
                                    !(!searchField.trim() && autoCompleteData.recentSearches?.length) && (
                                    <div className='campAutocompleteEmpty'>No results found</div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {searchAttempted && !searchField.trim() && <div className='campFieldError'>Please select a place</div>}
            </div>

            {/* =================================================
                DIVIDER
            ================================================= */}

            <div className='campDivider' />

            {/* =================================================
                DATE PICKER
            ================================================= */}

            <div
                className={`
                    campCol
                    campCol-date
                    campDateField
                    ${searchAttempted && (!startDate || !endDate) ? 'campFieldInvalid' : ''}
                `}
            >
                <div className='campDatePickerWrapper'>
                    {/* DATE LABELS */}

                    <div className='campDateLabels'>
                        <div className='campDateLabelBlock'>
                            <span className='campDateLabel'>Check-in</span>

                            <span className='campDateChevron'>›</span>
                        </div>

                        <div className='campDateLabelBlock campDateLabelRight'>
                            <span className='campDateLabel'>Check-out</span>

                            <span className='campDateChevron'>›</span>
                        </div>
                    </div>

                    {/* REAL REACT-DATES */}

                    <DateRangePicker
                        startDate={startDate}
                        startDateId={'campStartDate'}
                        endDate={endDate}
                        endDateId={'campEndDate'}
                        focusedInput={focusedInput}
                        onFocusChange={handleDateFocusChange}
                        onDatesChange={handleDatesChange}
                        startDatePlaceholderText={'Add date'}
                        endDatePlaceholderText={'Add date'}
                        noBorder
                        displayFormat={"D MMM'YY"}
                        showDefaultInputIcon={false}
                        orientation={isMobile && mobileCalendar !== 'popup' ? 'vertical' : 'horizontal'}
                        withFullScreenPortal={isMobile && mobileCalendar !== 'popup'}
                        numberOfMonths={isMobile ? 1 : 2}
                        {...(isMobile && mobileCalendar === 'popup'
                            ? { daySize: 40, anchorDirection: 'left', hideKeyboardShortcutsPanel: true }
                            : {})}
                        minimumNights={1}
                        isOutsideRange={(day) => day.isBefore(moment(), 'day')}
                    />
                </div>

                {searchAttempted && (!startDate || !endDate) && (
                    <div className='campFieldError campDateError'>Please select check-in and check-out dates</div>
                )}
            </div>

            {/* =================================================
                DIVIDER
            ================================================= */}

            <div className='campDivider' />

            {/* =================================================
                GUESTS
            ================================================= */}

            <div
                className={`
                    campCol
                    campCol-guests
                    campGuestField
                    ${searchAttempted && (!guestsConfirmed || !hasValidGuests(guests)) ? 'campFieldInvalid' : ''}
                `}
            >
                <span className='campColLabel'>Guests</span>

                <div className='campColValueRow'>
                    <CampGuestSelector
                        value={guests}
                        onChange={(nextGuests) => {
                            setGuests(nextGuests);

                            setGuestsConfirmed(false);
                        }}
                        open={openGuests}
                        onApply={() => {
                            setGuestsConfirmed(true);

                            setOpenGuests(false);
                        }}
                    />
                </div>

                {searchAttempted && (!guestsConfirmed || !hasValidGuests(guests)) && (
                    <div className='campFieldError campGuestError'>Select travellers and tap Apply</div>
                )}
            </div>

            {/* =================================================
                SEARCH
            ================================================= */}

            <button
                type='button'
                className='
                    hoilidayrSearchBtn
                    campSearchBtn
                    campBtn
                '
                onClick={() => handleSearch(searchField)}
            >
                Search
            </button>
        </div>
    );
};

export default Camp;
